import { EventEmitter, Injectable } from '@angular/core';
import {
  VisitModel,
  CreateVisitModel,
  EyeMeasurementModel,
  Eye,
  CreateEyeMeasurementModel,
  UpdateVisitModel,
  UpdateEyeMeasurementModel,
} from '../../global-data-models/visit.model';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { getEyeFromVisit } from '../../global-data-models/visit.model';
import { PatientModel } from '../../global-data-models/patient.model';
import { GlobalMessageService } from '../../global-services/global-message.service';
import { Misc } from '../../global-misc/misc';
import { PatientService } from '../../global-services/patient.service';
import { VisitService } from '../../global-services/visit.service';
import { Observable } from 'rxjs';
import { GlobalLoadingService } from '../../global-services/global-loading.service';
import { select } from 'survey-creator-core';

export type CrudVisitMode = 'create' | 'update';

export interface CrudVisitUiState {
  patientDetails: {
    id: string;
    fullName: string;
  };
  orgId: string;
  visitId?: string;
  form: FormGroup;
  crudMode: CrudVisitMode;
}

export type MeasurementRange = { min: number; max: number };
export const MEASUREMENT_RANGES: Record<string, MeasurementRange> = {
  sphere: { min: -20, max: 20 },
  cylinder: { min: -20, max: 20 },
  axis: { min: 0, max: 360 },
  axialLength: { min: 7, max: 50 },
  kValue: { min: -20, max: 20 },
};

function rangeValidator(range: MeasurementRange) {
  const min: number = range.min;
  const max: number = range.max;

  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value === null || control.value === undefined || control.value === '') {
      return null;
    }

    const numValue = Number(control.value);
    if (isNaN(numValue)) {
      return { invalidNumber: true };
    }

    if (numValue < min || numValue > max) {
      return { range: { min, max, actual: numValue } };
    }

    return null;
  };
}

function atLeastOneEyeValidator(control: AbstractControl): ValidationErrors | null {
  if (!control || !control.get) {
    return null;
  }

  const leftEye = control.get('leftEye');
  const rightEye = control.get('rightEye');

  if (!leftEye || !rightEye) {
    return null;
  }

  const leftEyeValues = leftEye.value;
  const rightEyeValues = rightEye.value;

  // Check if at least one eye has sphere AND (axial length OR k-value)
  const leftEyeValid = leftEyeValues.sphere !== null && leftEyeValues.sphere !== undefined && leftEyeValues.sphere !== '';

  const rightEyeValid = rightEyeValues.sphere !== null && rightEyeValues.sphere !== undefined && rightEyeValues.sphere !== '';

  if (!leftEyeValid && !rightEyeValid) {
    return { atLeastOneEyeRequired: true };
  }

  return null;
}

@Injectable({ providedIn: 'root' })
export class CrudVisitFacade {
  private _uiStateEvent: EventEmitter<CrudVisitUiState> = new EventEmitter<CrudVisitUiState>();
  public uiStateEvent$: Observable<CrudVisitUiState> = this._uiStateEvent.asObservable();

  public readonly EMPTY_ADD_VISIT_UI_STATE: CrudVisitUiState = {
    patientDetails: {
      id: '',
      fullName: 'Firstname Lastname',
    },
    orgId: '',
    visitId: '',
    form: this.createForm(),
    crudMode: 'create',
  };

  private uiState: CrudVisitUiState = this.EMPTY_ADD_VISIT_UI_STATE;

  public constructor(
    private readonly fb: FormBuilder,
    private readonly messageService: GlobalMessageService,
    private readonly patientService: PatientService,
    private readonly visitService: VisitService,
    private readonly loadingService: GlobalLoadingService,
  ) {}

  public async begin(_crudMode: CrudVisitMode, _orgId: string, _patientId: string, _visitId?: string) {
    this.loadingService.show();
    try {
      const patientData: PatientModel = await this.patientService.getPatient(_orgId, _patientId);
      let visitData: VisitModel | undefined = undefined;

      if (_crudMode === 'update') {
        visitData = await this.visitService.getPatientVisit(_orgId, _patientId, _visitId!);
        console.log(visitData);
      }

      const _form = this.createForm(visitData);
      const _fullName: string = `${patientData.firstName} ${patientData.lastName}`;

      this.uiState = {
        patientDetails: {
          id: _patientId,
          fullName: _fullName,
        },
        orgId: _orgId,
        visitId: _visitId,
        form: _form,
        crudMode: _crudMode,
      };
    } catch (error: unknown) {
      this.messageService.showError('Get Patient Details Error', Misc.getErrorMessage(error));
      this.uiState = this.EMPTY_ADD_VISIT_UI_STATE;
    } finally {
      this._uiStateEvent.emit(this.uiState);
      this.loadingService.hide();
    }
  }

  private formToVisitModel(): CreateVisitModel | UpdateVisitModel {
    const values = this.uiState!.form.getRawValue();

    const measurementsArray: CreateEyeMeasurementModel[] | UpdateEyeMeasurementModel[] = [];

    function getJsonMeasurementValue(value: any): number | undefined {
      return value === 0 ? value : (value ?? undefined);
    }

    for (const measurement of [values.leftEye, values.rightEye]) {
      measurementsArray.push({
        id: this.uiState.crudMode === 'update' ? measurement.id : undefined,
        visit_id: this.uiState.crudMode === 'update' ? measurement.visit_id : undefined,
        eyeSide: measurement.eyeSide,
        measurementType: measurement.measurementType,
        sphere: getJsonMeasurementValue(measurement.sphere),
        cylinder: getJsonMeasurementValue(measurement.cylinder),
        axis: getJsonMeasurementValue(measurement.axis),
        axialLength: getJsonMeasurementValue(measurement.axialLength),
        kValue: getJsonMeasurementValue(measurement.kValue),
      });
    }

    const dateValue: Date = values.visitData.date;
    const selectedDate = new Date(Date.UTC(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate(), 12, 0, 0, 0));

    console.log('date in normalized utc = ', selectedDate.toUTCString());

    let finalData = {
      date: selectedDate.toISOString(),
      eyeMeasurements: measurementsArray,
    };

    if (this.uiState.crudMode === 'create') {
      return finalData as CreateVisitModel;
    }

    if (this.uiState.crudMode === 'update') {
      return { ...finalData, id: this.uiState.visitId! } as UpdateVisitModel;
    }

    throw new Error('Reached code that should be unreachable in formToVisitModel');
  }

  private createEyeForm(eyeSide: Eye, visitData?: VisitModel): FormGroup {
    const eyeData: EyeMeasurementModel | undefined = visitData ? getEyeFromVisit(visitData!, eyeSide) : undefined;

    return this.fb.group({
      id: [{ value: eyeData?.id, disabled: true }],
      visit_id: [{ value: visitData?.id, disabled: true }],

      eyeSide: [{ value: eyeData?.eyeSide ?? eyeSide!, disabled: true }],
      measurementType: [eyeData?.measurementType ?? 'Cycloplegic'],
      sphere: [eyeData?.sphere ?? null, rangeValidator(MEASUREMENT_RANGES['sphere'])],
      cylinder: [eyeData?.cylinder ?? null, rangeValidator(MEASUREMENT_RANGES['cylinder'])],
      axis: [eyeData?.axis ?? null, rangeValidator(MEASUREMENT_RANGES['axis'])],
      axialLength: [eyeData?.axialLength ?? null, rangeValidator(MEASUREMENT_RANGES['axialLength'])],
      kValue: [eyeData?.kValue ?? null, rangeValidator(MEASUREMENT_RANGES['kValue'])],
    });
  }

  public createForm(visitData?: VisitModel): FormGroup {
    const form = this.fb.group(
      {
        visitData: this.fb.group({
          date: [visitData?.date ? new Date(visitData.date) : new Date(), Validators.required],
        }),

        leftEye: this.createEyeForm(Eye.LEFT, visitData),
        rightEye: this.createEyeForm(Eye.RIGHT, visitData),
      },
      { validators: [atLeastOneEyeValidator] },
    );

    // Sync measurement type between left and right eyes
    const leftEyeForm = form.get('leftEye') as FormGroup;
    const rightEyeForm = form.get('rightEye') as FormGroup;

    leftEyeForm.get('measurementType')?.valueChanges.subscribe((value) => {
      const rightEyeCurrentValue = rightEyeForm.get('measurementType')?.value;
      if (rightEyeCurrentValue !== value) {
        rightEyeForm.get('measurementType')?.setValue(value, { emitEvent: false });
      }
    });

    rightEyeForm.get('measurementType')?.valueChanges.subscribe((value) => {
      const leftEyeCurrentValue = leftEyeForm.get('measurementType')?.value;
      if (leftEyeCurrentValue !== value) {
        leftEyeForm.get('measurementType')?.setValue(value, { emitEvent: false });
      }
    });

    return form;
  }

  public shouldShowWarning(): boolean {
    const form = this.uiState!.form;
    const leftEye = form.get('leftEye')!;
    const rightEye = form.get('rightEye')!;

    const leftEyeAxialLength = leftEye.get('axialLength')!.value;
    const leftEyeKValue = leftEye.get('kValue')!.value;
    const leftEyeSphere = leftEye.get('sphere')!.value;

    const rightEyeAxialLength = rightEye.get('axialLength')!.value;
    const rightEyeKValue = rightEye.get('kValue')!.value;
    const rightEyeSphere = rightEye.get('sphere')!.value;

    if (!leftEyeAxialLength && !leftEyeKValue && leftEyeSphere) {
      return true;
    }

    if (!rightEyeAxialLength && !rightEyeKValue && rightEyeSphere) {
      return true;
    }

    return false;
  }

  public async onSubmit(): Promise<boolean> {
    const visit: CreateVisitModel | UpdateVisitModel = this.formToVisitModel();

    this.loadingService.show();
    try {
      if (this.uiState.crudMode === 'create') {
        await this.visitService.createPatientVisit(this.uiState.orgId, this.uiState.patientDetails.id, visit as CreateVisitModel);
        this.messageService.showSuccess('Visit Added', 'Patient visit has been successfully added.');
      }

      if (this.uiState.crudMode === 'update') {
        await this.visitService.updatePatientVisit(this.uiState.orgId, this.uiState.patientDetails.id, this.uiState.visitId!, visit as UpdateVisitModel);
        this.messageService.showSuccess('Visit Updated', 'Patient visit has been successfully updated.');
      }

      return true;
    } catch (error: unknown) {
      console.error('Error submitting visit form:', error);
      this.messageService.showError('Form Submit Error', Misc.getErrorMessage(error));
      return false;
    } finally {
      this.loadingService.hide();
    }
  }
}
